/**
 * Cable profiles -- group 06.
 *
 * Gotham Audio, Neotech, DH Labs, Inakustik, Oehlbach, Goldkabel, Vovox and
 * Sommer Cable.
 *
 * A note on the electrical figures in this group. Several of these houses are
 * pro-audio suppliers that do publish per-metre data on their datasheets, but a
 * published figure is only worth passing in if it is recalled exactly. A number
 * that is merely class-typical is a derivation wearing a datasheet costume, and
 * tagging it MANUFACTURER_SPEC would be a fabrication of provenance rather than
 * of physics. So every entry here states the facts that are actually on the
 * packaging -- conductor material, cross-section, geometry, shield type,
 * dielectric, current rating, standard met -- and lets the kit derive R, L and C
 * from gauge, material and geometry. The derivation is documented and the
 * confidence tag says DERIVED, which is the truth.
 *
 * Power, USB and Ethernet entries describe construction only. The engine applies
 * no signal processing to those types, so a note claiming an audible effect
 * would be describing something the software does not do.
 */

import type { CableProfile } from '../../types/hardware';
import { makeCable } from '../cableKit';
import { usd } from '../kit';

export const CABLES_06: CableProfile[] = [
  // =========================================================================
  // Gotham Audio -- Switzerland. Pro-audio house whose signature is the
  // Reussen shield: two counter-wound spiral copper layers instead of a braid.
  // =========================================================================
  makeCable({
    mfr: 'Gotham Audio',
    model: 'GAC-2 Ultra Pro',
    cableType: 'interconnect',
    price: usd(70, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m terminated pair built from bulk cable.'),
    priceLengthM: 1,
    country: 'Switzerland',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    shielding: 'Reussen: two counter-wound spiral copper layers, near 100 percent coverage',
    dielectric: 'Polyethylene',
    termination: ['XLR', 'RCA'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8fa6b8',
    tags: ['pro-audio', 'reussen-shield', 'bulk-cable', 'studio-standard'],
    notes:
      'Two stranded copper conductors of 0.22 mm2, about 24 AWG each, twisted under the Reussen shield. The counter-wound spirals give braid-like coverage without a braid to work-harden, which is a mechanical property rather than an electrical one. At the derived capacitance a 1 m run loads a 600 ohm output stage with a corner around 4 MHz, so the computed in-band deviation is nil.',
  }),

  makeCable({
    mfr: 'Gotham Audio',
    model: 'GAC-2 Pur',
    cableType: 'interconnect',
    price: usd(80, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m terminated pair built from bulk cable.'),
    priceLengthM: 1,
    country: 'Switzerland',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    shielding: 'Reussen spiral copper shield',
    dielectric: 'Polyethylene, polyurethane outer jacket',
    termination: ['XLR', 'RCA'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8fa6b8',
    tags: ['pro-audio', 'reussen-shield', 'pur-jacket'],
    notes:
      'The two-conductor design in a polyurethane jacket rather than PVC. PUR resists abrasion and stays flexible in the cold, which matters to a touring crew and is invisible to the electrical model: conductor size, spacing and shield are unchanged, so the derived R, L and C are the same as the standard jacket version.',
  }),

  makeCable({
    mfr: 'Gotham Audio',
    model: 'GAC-4/1 Ultra Pro',
    cableType: 'interconnect',
    price: usd(90, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m terminated pair built from bulk cable.'),
    priceLengthM: 1,
    country: 'Switzerland',
    conductor: 'ofc',
    geometry: 'star-quad',
    gauge: 23,
    shielding: 'Reussen spiral copper shield',
    dielectric: 'Polyethylene',
    termination: ['XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8fa6b8',
    tags: ['pro-audio', 'reussen-shield', 'hum-rejection'],
    notes:
      'Four 0.14 mm2 conductors, about 26 AWG each, laid in a star quad with opposite conductors paralleled. That halves the loop resistance relative to one conductor per leg, which is why the gauge here is entered as the 23 AWG equivalent rather than the individual wire size. The quad also puts the two halves of each leg on opposite sides of the axis, so a magnetically induced voltage appears equally in both and cancels at the receiver.',
  }),

  makeCable({
    mfr: 'Gotham Audio',
    model: 'GAC-4/1 Pur',
    cableType: 'interconnect',
    price: usd(100, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m terminated pair built from bulk cable.'),
    priceLengthM: 1,
    country: 'Switzerland',
    conductor: 'ofc',
    geometry: 'star-quad',
    gauge: 23,
    shielding: 'Reussen spiral copper shield',
    dielectric: 'Polyethylene, polyurethane outer jacket',
    termination: ['XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8fa6b8',
    tags: ['pro-audio', 'reussen-shield', 'pur-jacket', 'hum-rejection'],
    notes:
      'Star quad in a polyurethane jacket. Same four 0.14 mm2 conductors paralleled in opposing pairs for a 23 AWG equivalent leg, same shield, tougher outer. The jacket change is mechanical only and the derived electrical figures are identical.',
  }),

  makeCable({
    mfr: 'Gotham Audio',
    model: 'GAC-1 Ultra Pro',
    cableType: 'interconnect',
    price: usd(60, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m terminated pair built from bulk cable.'),
    priceLengthM: 1,
    country: 'Switzerland',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 24,
    shielding: 'Reussen spiral copper shield used as the signal return',
    dielectric: 'Polyethylene',
    termination: ['RCA', 'TS jack'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8fa6b8',
    tags: ['pro-audio', 'unbalanced', 'reussen-shield'],
    notes:
      'Single 0.22 mm2 core inside the Reussen shield, which here carries the return current as well as screening. Coaxial geometry puts the return concentric with the signal, so the enclosed loop area is small and the inductance low, with capacitance correspondingly higher than a spaced pair. Over 1 m that capacitance sits against the source impedance and the corner still lands in the megahertz.',
  }),

  makeCable({
    mfr: 'Gotham Audio',
    model: 'GAC-3',
    cableType: 'interconnect',
    price: usd(75, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m terminated pair built from bulk cable.'),
    priceLengthM: 1,
    country: 'Switzerland',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    shielding: 'Reussen spiral copper shield',
    dielectric: 'Polyethylene',
    termination: ['XLR', 'RCA'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8fa6b8',
    tags: ['pro-audio', 'three-conductor', 'reussen-shield'],
    notes:
      'Three insulated conductors under one Reussen shield, which lets a balanced run keep its ground on a separate wire and leave the shield connected at one end only. The signal pair is still a twisted pair electrically, so the derived inductance and capacitance follow that geometry; the third conductor changes the wiring options rather than the transfer function.',
  }),

  // =========================================================================
  // Neotech -- Taiwan. UP-OCC continuous-cast copper and silver, drawn by
  // Yeu Shiuan. Sold as bulk cable and as finished leads.
  // =========================================================================
  makeCable({
    mfr: 'Neotech',
    model: 'NEI-1001 MKIII',
    cableType: 'interconnect',
    price: usd(180, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated RCA pair.'),
    priceLengthM: 1,
    country: 'Taiwan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Aluminium foil plus copper braid',
    dielectric: 'Polyethylene',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d4a15a',
    tags: ['up-occ', 'entry-level', 'shielded'],
    notes:
      'Continuous-cast copper drawn so that grain boundaries run along the wire rather than across it, in a twisted pair with foil and braid over it. The casting process is a real manufacturing difference; its effect on bulk resistivity is under one percent against ordinary OFC, and that one percent is the whole of what the engine can act on. Over a 1 m run into a 10 kilohm input the loop resistance is about a tenth of an ohm and the divider it forms is unmeasurable.',
  }),

  makeCable({
    mfr: 'Neotech',
    model: 'NEI-3001 MKIII',
    cableType: 'interconnect',
    price: usd(320, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Taiwan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Aluminium foil plus copper braid',
    dielectric: 'Polyethylene',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d4a15a',
    tags: ['up-occ', 'shielded'],
    notes:
      'A heavier version of the same twisted-pair build, moving up a gauge to roughly 0.4 mm2 per conductor. Interconnect resistance is already far below anything a line input notices, so the larger conductor buys mechanical robustness rather than a change the model can report.',
  }),

  makeCable({
    mfr: 'Neotech',
    model: 'NEI-3002 MKIII',
    cableType: 'interconnect',
    price: usd(480, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Taiwan',
    conductor: 'occ-copper',
    geometry: 'star-quad',
    gauge: 20,
    shielding: 'Aluminium foil plus copper braid',
    dielectric: 'Polyethylene',
    termination: ['XLR', 'RCA'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d4a15a',
    tags: ['up-occ', 'shielded', 'hum-rejection'],
    notes:
      'Four conductors in a quad, paralleled in opposing pairs, which is the geometry that cancels magnetically induced hum in a balanced run. Quad spacing sits between a twisted pair and a coax on both inductance and capacitance, and the kit places it there rather than assigning it a character.',
  }),

  makeCable({
    mfr: 'Neotech',
    model: 'NEI-5001',
    cableType: 'interconnect',
    price: usd(1400, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Taiwan',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Aluminium foil plus copper braid',
    dielectric: 'PTFE',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d4a15a',
    tags: ['up-occ', 'silver', 'ptfe'],
    notes:
      'Continuous-cast pure silver in PTFE. Silver has about eight percent lower resistivity than copper, so over a 1 m 22 AWG run the loop resistance falls by roughly 0.008 ohm against an input impedance of tens of kilohms. That is the entire electrical difference the material makes and the engine computes it rather than dramatising it.',
  }),

  makeCable({
    mfr: 'Neotech',
    model: 'NES-1002',
    cableType: 'speaker',
    price: usd(220, 'APPROXIMATE', 2024, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'Taiwan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 14,
    dielectric: 'Polyethylene',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d4a15a',
    tags: ['up-occ', 'entry-level'],
    notes:
      'Two 14 AWG continuous-cast copper conductors twisted together under a PE jacket. A 3 m pair contributes about 0.05 ohm of loop resistance, which is 0.05 dB of loss into 8 ohms and about 0.14 dB where the loudspeaker impedance dips to 3 ohms. Both figures are small and both are frequency dependent through the load, which is exactly what the engine plots.',
  }),

  makeCable({
    mfr: 'Neotech',
    model: 'NES-3002',
    cableType: 'speaker',
    price: usd(650, 'APPROXIMATE', 2024, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'Taiwan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    dielectric: 'Polyethylene',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d4a15a',
    tags: ['up-occ', 'mid-line'],
    notes:
      'Same construction stepped up to roughly 3.3 mm2 per leg. Doubling conductor area halves loop resistance, so a 3 m pair drops to about 0.031 ohm and the worst-case loss into a 4 ohm minimum falls to under 0.07 dB. This is the one axis on which a speaker cable reliably earns its money and it is bought entirely with copper.',
  }),

  makeCable({
    mfr: 'Neotech',
    model: 'NES-3003',
    cableType: 'speaker',
    price: usd(950, 'APPROXIMATE', 2024, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'Taiwan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    dielectric: 'Polyethylene',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d4a15a',
    tags: ['up-occ', 'heavy-gauge'],
    notes:
      'The heavy-gauge member of the line at about 5.3 mm2 per leg. Loop resistance over 3 m works out near 0.02 ohm, so the series loss into a 4 ohm load is around 0.04 dB and the amplifier damping factor at the terminals is barely degraded. Past this point extra copper returns diminishing fractions of a decibel.',
  }),

  makeCable({
    mfr: 'Neotech',
    model: 'NEP-3001',
    cableType: 'power',
    price: usd(250, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m assembled cord.'),
    priceLengthM: 1.5,
    country: 'Taiwan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 14,
    currentRatingA: 15,
    shielding: 'Aluminium foil with a drain wire',
    dielectric: 'Polyethylene',
    termination: ['IEC C13', 'Schuko', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d4a15a',
    tags: ['up-occ', 'shielded', 'iec-c13'],
    notes:
      'Three 2.0 mm2 continuous-cast copper conductors, roughly 14 AWG, twisted and foil-screened with a drain wire. Rated for 15 A, which covers any normal source component and most integrated amplifiers. The engine applies no signal processing to power cords, so choosing this changes the chain description and the price and leaves the audio bit-identical.',
  }),

  makeCable({
    mfr: 'Neotech',
    model: 'NEP-3200',
    cableType: 'power',
    price: usd(380, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m assembled cord.'),
    priceLengthM: 1.5,
    country: 'Taiwan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 20,
    shielding: 'Aluminium foil plus copper braid',
    dielectric: 'Polyethylene',
    termination: ['IEC C13', 'IEC C19', 'Schuko', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d4a15a',
    tags: ['up-occ', 'shielded', 'high-current'],
    notes:
      'The heavier mains cable in the range: three conductors of about 3.3 mm2, foil and braid, 20 A rating and a C19 option for amplifiers with the larger inlet. Gauge and rating are the specification that matters here, and they matter for voltage drop under sustained current draw, not for the signal. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Neotech',
    model: 'NEUB-1001',
    cableType: 'usb',
    price: usd(150, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m assembly.'),
    priceLengthM: 1.5,
    country: 'Taiwan',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d4a15a',
    tags: ['up-occ', 'usb-2.0', 'shielded'],
    notes:
      'A 90 ohm differential pair in continuous-cast copper with separate power conductors, foil and braid over the lot. It either meets the USB 2.0 eye-diagram requirement or it does not; the data is packetised and checked, and an asynchronous DAC clocks itself. The engine applies no signal processing to USB cables.',
  }),

  // =========================================================================
  // DH Labs -- United States. Silver Sonic: silver-coated OFC conductors,
  // PTFE and air-articulated dielectrics.
  // =========================================================================
  makeCable({
    mfr: 'DH Labs',
    model: 'BL-1 Series II',
    cableType: 'interconnect',
    price: usd(160, 'CURRENT_RETAIL', 2024, 'Price for a 1 m terminated RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Overlapping foil with a drain wire',
    dielectric: 'PTFE',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0c8d0',
    tags: ['silver-coated', 'ptfe', 'shielded'],
    notes:
      'Silver-coated OFC in PTFE, twisted and foil-screened. The silver layer is a skin plating: at audio frequencies conduction is essentially uniform through the section, so the DC path is the copper core and the resistivity used here is copper. PTFE has a lower permittivity than PE, which is a genuine reason for capacitance per metre to come out on the low side of the twisted-pair range.',
  }),

  makeCable({
    mfr: 'DH Labs',
    model: 'Air Matrix Cryo',
    cableType: 'interconnect',
    price: usd(450, 'CURRENT_RETAIL', 2024, 'Price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'air-tube',
    gauge: 21,
    shielding: 'Foil with a drain wire',
    dielectric: 'Air-articulated PTFE',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0c8d0',
    tags: ['silver-coated', 'air-dielectric', 'cryo-treated'],
    notes:
      'The conductor is held away from the surrounding structure by a PTFE lattice so that most of the volume between conductor and shield is air. Air has a relative permittivity near 1 against about 2.1 for solid PTFE, and capacitance scales with permittivity, so the per-metre capacitance drops and the inductance rises with the wider spacing. The cryogenic step is part of the manufacturing description; no electrical consequence is claimed for it here because none is derivable.',
  }),

  makeCable({
    mfr: 'DH Labs',
    model: 'Revelation',
    cableType: 'interconnect',
    price: usd(950, 'CURRENT_RETAIL', 2024, 'Price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'air-tube',
    gauge: 20,
    shielding: 'Foil with a drain wire',
    dielectric: 'Air-articulated PTFE',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    provenanceNote:
      'Series placement and the air-articulated PTFE construction follow the maker description of the top interconnect line; the exact conductor count and cross-section are not stated publicly, so the gauge here is an estimate from the line above it. No resistance, inductance or capacitance figures are published, so all three are derived from gauge, material and geometry.',
    accent: '#c0c8d0',
    tags: ['silver-coated', 'air-dielectric', 'flagship'],
    notes:
      'The largest of the air-articulated interconnects, with the conductor suspended in a mostly-air dielectric and a foil screen outside it. Wider conductor-to-shield spacing lowers capacitance and raises inductance; at interconnect currents the inductance is irrelevant and the capacitance is the only term that reaches the model, through the low-pass it forms with the source impedance.',
  }),

  makeCable({
    mfr: 'DH Labs',
    model: 'T-14',
    cableType: 'speaker',
    price: usd(330, 'APPROXIMATE', 2024, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 14,
    dielectric: 'Polyethylene',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0c8d0',
    tags: ['silver-coated', 'entry-level'],
    notes:
      'Silver-coated OFC at 14 AWG per leg, twisted. Over 3 m the loop resistance is about 0.05 ohm: 0.05 dB into 8 ohms, closer to 0.14 dB at a 3 ohm impedance minimum. Because the loss tracks the loudspeaker impedance curve it is not a flat level change, and that frequency dependence is the whole of what a speaker cable does at this length.',
  }),

  makeCable({
    mfr: 'DH Labs',
    model: 'Deity',
    cableType: 'speaker',
    price: usd(700, 'APPROXIMATE', 2024, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    dielectric: 'Polyethylene',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0c8d0',
    tags: ['silver-coated', 'mid-line'],
    notes:
      'Twelve AWG per leg in silver-coated OFC. The step from 14 to 12 AWG cuts loop resistance by about a third, to roughly 0.031 ohm over 3 m. On a loudspeaker with a benign impedance curve that is inaudible arithmetic; on a 3 ohm minimum with a long run it is the difference the engine actually reports.',
  }),

  makeCable({
    mfr: 'DH Labs',
    model: 'Q-10 Signature',
    cableType: 'speaker',
    price: usd(1100, 'APPROXIMATE', 2024, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    dielectric: 'Polyethylene',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0c8d0',
    tags: ['silver-coated', 'heavy-gauge', 'flagship'],
    notes:
      'Ten AWG equivalent per polarity, built from multiple insulated silver-coated conductors rather than one thick strand so the cable stays flexible. Loop resistance over 3 m is near 0.02 ohm, which is about 0.04 dB into 4 ohms. Bi-wire and long-run installations are where a conductor this large stops being surplus.',
  }),

  makeCable({
    mfr: 'DH Labs',
    model: 'Power Plus',
    cableType: 'power',
    price: usd(230, 'CURRENT_RETAIL', 2024, 'Price for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    shielding: 'Foil with a drain wire',
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0c8d0',
    tags: ['silver-coated', 'shielded', 'iec-c13'],
    notes:
      'Twelve AWG silver-coated conductors, foil screen, moulded C13 and 5-15 ends, 15 A. That is a well-specified mains cord and the specification is where the description stops: the engine applies no signal processing to power cables, so this entry changes the price of the chain and nothing in the audio.',
  }),

  makeCable({
    mfr: 'DH Labs',
    model: 'Encore',
    cableType: 'power',
    price: usd(500, 'CURRENT_RETAIL', 2024, 'Price for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 20,
    shielding: 'Foil plus braid',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0c8d0',
    tags: ['silver-coated', 'shielded', 'high-current'],
    notes:
      'Heavier conductors than the Power Plus at roughly 11 AWG, with foil and braid and a 20 A rating, available with the C19 inlet used by large amplifiers. Construction only: no signal-path model is applied to this cable type.',
  }),

  makeCable({
    mfr: 'DH Labs',
    model: 'Corona',
    cableType: 'power',
    price: usd(900, 'CURRENT_RETAIL', 2024, 'Price for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 20,
    shielding: 'Foil plus braid',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0c8d0',
    tags: ['silver-coated', 'shielded', 'flagship'],
    notes:
      'The largest cord in the range, 10 AWG per conductor with a full foil-and-braid screen and 20 A connectors. A 1.8 m run of 10 AWG drops on the order of ten millivolts at a few amps, which is good engineering. It is also the end of what can honestly be said, because the engine models no effect for power cords at all.',
  }),

  makeCable({
    mfr: 'DH Labs',
    model: 'Silver Sonic USB',
    cableType: 'usb',
    price: usd(130, 'CURRENT_RETAIL', 2024, 'Price for a 1 m assembly.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0c8d0',
    tags: ['silver-coated', 'usb-2.0', 'shielded'],
    notes:
      'Silver-coated 24 AWG differential pair held to the 90 ohm USB requirement, with separate 24 AWG power conductors and a foil-plus-braid screen. Compliance is the whole specification for a digital lead of this kind, and the engine applies no signal processing to USB.',
  }),

  // =========================================================================
  // Inakustik -- Germany. Star / Premium / Excellence / Referenz tiers, with
  // the Referenz AIR cables built around an air-chamber dielectric.
  // =========================================================================
  makeCable({
    mfr: 'Inakustik',
    model: 'Excellence LS-1102',
    cableType: 'speaker',
    price: usd(450, 'APPROXIMATE', 2024, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    dielectric: 'Polyethylene',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e2542c',
    tags: ['entry-level', 'multi-strand'],
    notes:
      'Two multi-strand OFC conductors of about 2.5 mm2, twisted under a PE jacket. A 3 m pair adds roughly 0.04 ohm of loop resistance, so the divider against an 8 ohm load costs about 0.04 dB and about 0.09 dB where the load falls to 4 ohms.',
  }),

  makeCable({
    mfr: 'Inakustik',
    model: 'Excellence LS-1202',
    cableType: 'speaker',
    price: usd(700, 'APPROXIMATE', 2024, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    dielectric: 'Polyethylene',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e2542c',
    tags: ['mid-line', 'multi-strand'],
    notes:
      'The next cross-section up, near 3.3 mm2 per leg. Loop resistance over 3 m is about 0.031 ohm. Twisting the two legs together keeps the enclosed loop area small, which is why a twisted pair sits well below a spaced parallel run on inductance and above it on capacitance.',
  }),

  makeCable({
    mfr: 'Inakustik',
    model: 'Referenz LS-1603',
    cableType: 'speaker',
    price: usd(1400, 'APPROXIMATE', 2024, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 10,
    dielectric: 'Polyethylene',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e2542c',
    tags: ['reference-line', 'heavy-gauge'],
    notes:
      'Reference-tier multi-strand OFC at roughly 5 mm2 per leg. Over 3 m that is about 0.02 ohm of loop resistance, or 0.04 dB into 4 ohms. At this conductor size the series resistance has stopped being the limiting term in any normal domestic run.',
  }),

  makeCable({
    mfr: 'Inakustik',
    model: 'Referenz LS-2404 AIR',
    cableType: 'speaker',
    price: usd(3000, 'APPROXIMATE', 2024, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'air-tube',
    gauge: 10,
    dielectric: 'Air-chamber helix, polyethylene support',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e2542c',
    tags: ['reference-line', 'air-dielectric'],
    notes:
      'Conductors carried in a helical support so the space around them is mostly air. Air-spaced construction lowers capacitance per metre and, because the conductors end up further apart, raises loop inductance. Over 3 m the derived inductance puts about a third of an ohm of reactance in series at 20 kHz, which against a 4 ohm load computes to under a tenth of a decibel of high-frequency loss -- the trade is real, small, and it is arithmetic rather than opinion.',
  }),

  makeCable({
    mfr: 'Inakustik',
    model: 'Referenz LS-4004 AIR',
    cableType: 'speaker',
    price: usd(6000, 'APPROXIMATE', 2024, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'air-tube',
    gauge: 8,
    dielectric: 'Air-chamber helix, polyethylene support',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e2542c',
    tags: ['reference-line', 'air-dielectric', 'flagship'],
    notes:
      'The largest air-dielectric loudspeaker cable in the range, with an 8 AWG equivalent per leg giving about 0.012 ohm of loop resistance over 3 m. The engineering is genuine and the price is what a hand-assembled air-chamber construction in this quantity of copper costs; the modelled difference against the 10 AWG version below it is a few hundredths of a decibel at the impedance minimum.',
  }),

  makeCable({
    mfr: 'Inakustik',
    model: 'Excellence NF-1202',
    cableType: 'interconnect',
    price: usd(350, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Foil plus copper braid',
    dielectric: 'Polyethylene',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e2542c',
    tags: ['shielded', 'mid-line'],
    notes:
      'Twisted OFC pair with foil and braid, offered single-ended and balanced. At the derived capacitance a 1 m run against a 600 ohm source has its corner in the megahertz; against a valve preamplifier with a 5 kilohm output impedance and a 3 m run it would be near 150 kHz, which is the case where an interconnect starts to matter and why the engine computes it per length.',
  }),

  makeCable({
    mfr: 'Inakustik',
    model: 'Referenz NF-1302',
    cableType: 'interconnect',
    price: usd(700, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Foil plus copper braid',
    dielectric: 'Polyethylene',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e2542c',
    tags: ['reference-line', 'shielded'],
    notes:
      'Reference-tier twisted pair with a larger conductor and a double screen. The screen is what earns its keep on an unbalanced RCA run, where any current induced in the return shows up as signal; the conductor size is well past the point where interconnect resistance is measurable at the input of a line stage.',
  }),

  makeCable({
    mfr: 'Inakustik',
    model: 'Referenz NF-2404 AIR',
    cableType: 'interconnect',
    price: usd(1800, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'air-tube',
    gauge: 21,
    shielding: 'Foil plus copper braid',
    dielectric: 'Air-chamber helix, polyethylene support',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e2542c',
    tags: ['reference-line', 'air-dielectric', 'flagship'],
    notes:
      'The air-chamber construction applied to a line-level cable. Lower dielectric constant means lower capacitance per metre, and for an interconnect capacitance is the only one of the three terms with any leverage. The corner it forms with a normal solid-state output impedance moves from several megahertz to rather more, which the engine will report as no in-band change whatsoever.',
  }),

  makeCable({
    mfr: 'Inakustik',
    model: 'AC-1502',
    cableType: 'power',
    price: usd(300, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    shielding: 'Aluminium foil with a drain wire',
    termination: ['IEC C13', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e2542c',
    tags: ['shielded', 'schuko', 'iec-c13'],
    notes:
      'Three 2.5 mm2 OFC conductors, foil screen, Schuko to C13, rated 16 A to the European domestic standard. A properly made cord to a published rating. The engine applies no signal processing to power cables, and this entry is here to be described accurately rather than to do anything.',
  }),

  makeCable({
    mfr: 'Inakustik',
    model: 'Referenz AC-2404 AIR',
    cableType: 'power',
    price: usd(1300, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'air-tube',
    gauge: 11,
    currentRatingA: 16,
    shielding: 'Aluminium foil with a drain wire',
    dielectric: 'Air-chamber helix, polyethylene support',
    termination: ['IEC C13', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e2542c',
    tags: ['reference-line', 'air-dielectric', 'schuko'],
    notes:
      'The air-chamber construction carried over to a mains cord, with roughly 4 mm2 conductors and a 16 A rating. It is a substantial and carefully built cable. It is also a mains cord sitting upstream of a rectifier and a reservoir bank, and the engine applies zero DSP to it, which this database states plainly rather than working around.',
  }),

  makeCable({
    mfr: 'Inakustik',
    model: 'Premium High Speed USB 2.0',
    cableType: 'usb',
    price: usd(45, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m assembly.'),
    priceLengthM: 1.5,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e2542c',
    tags: ['usb-2.0', 'shielded', 'entry-level'],
    notes:
      'A conventional compliant USB 2.0 assembly: 24 AWG twisted data pair at 90 ohms, 24 AWG bus power conductors, foil and braid, moulded connectors. It meets the standard, which is the only requirement a USB lead has. No signal processing is applied.',
  }),

  makeCable({
    mfr: 'Inakustik',
    model: 'Premium CAT6 Patch Cable',
    cableType: 'ethernet',
    price: usd(40, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m patch lead.'),
    priceLengthM: 1.5,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 100,
    digitalStandard: 'Cat 6, 250 MHz',
    shielding: 'S/FTP, foiled pairs in an overall braid',
    termination: ['RJ45'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e2542c',
    tags: ['cat6', 'shielded'],
    notes:
      'Four foiled 24 AWG twisted pairs in an overall braid, terminated in shielded RJ45 plugs, certified to Cat 6. Ethernet frames are checksummed and retransmitted on error and a streamer buffers seconds of playback, so there is no analog path between the ends. The engine applies no signal processing here.',
  }),

  // =========================================================================
  // Oehlbach -- Germany. Volume consumer and custom-install cable.
  // =========================================================================
  makeCable({
    mfr: 'Oehlbach',
    model: 'Speaker Wire 15',
    cableType: 'speaker',
    price: usd(45, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m pair cut from bulk cable.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 15,
    dielectric: 'PVC',
    termination: ['bare', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e2a03f',
    tags: ['bulk-cable', 'entry-level', 'zip-cord'],
    notes:
      'Twin 1.5 mm2 OFC in the familiar bonded figure-of-eight profile. Two conductors side by side and close together give low loop inductance and comparatively high capacitance, which is the correct trade for a loudspeaker load. A 3 m pair contributes about 0.06 ohm; a 10 m run of the same wire would contribute 0.21 ohm, and that is where the gauge choice starts to show in the engine output.',
  }),

  makeCable({
    mfr: 'Oehlbach',
    model: 'Speaker Wire 20',
    cableType: 'speaker',
    price: usd(60, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m pair cut from bulk cable.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 14,
    dielectric: 'PVC',
    termination: ['bare', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e2a03f',
    tags: ['bulk-cable', 'zip-cord'],
    notes:
      'The 2.0 mm2 version of the same bonded pair, close to 14 AWG. Loop resistance over 3 m is about 0.05 ohm, so the loss into 8 ohms is around 0.05 dB. For most domestic runs into a well-behaved loudspeaker this is already sufficient copper.',
  }),

  makeCable({
    mfr: 'Oehlbach',
    model: 'Speaker Wire 40',
    cableType: 'speaker',
    price: usd(110, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m pair cut from bulk cable.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 11,
    dielectric: 'PVC',
    termination: ['bare', 'banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e2a03f',
    tags: ['bulk-cable', 'heavy-gauge', 'zip-cord'],
    notes:
      'Four square millimetres per leg, roughly 11 AWG, in the same bonded profile. This is the size to reach for on long in-wall runs: 10 m contributes about 0.08 ohm rather than the 0.21 ohm of the 1.5 mm2 wire, and that difference is worth about 0.14 dB into a 4 ohm load.',
  }),

  makeCable({
    mfr: 'Oehlbach',
    model: 'NF 1 Master',
    cableType: 'interconnect',
    price: usd(80, 'CURRENT_RETAIL', 2024, 'Price for a 1 m terminated RCA pair.'),
    priceLengthM: 1,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Spiral copper shield',
    dielectric: 'Polyethylene',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e2a03f',
    tags: ['unbalanced', 'shielded', 'entry-level'],
    notes:
      'Conventional coaxial RCA lead: stranded OFC centre, PE dielectric, spiral copper screen carrying the return. Concentric return means small loop area and therefore low inductance, with capacitance higher than a spaced pair as the trade. At 1 m neither term reaches the audio band against a normal source impedance.',
  }),

  makeCable({
    mfr: 'Oehlbach',
    model: 'NF 14 Master',
    cableType: 'interconnect',
    price: usd(140, 'CURRENT_RETAIL', 2024, 'Price for a 1 m terminated RCA pair.'),
    priceLengthM: 1,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 21,
    shielding: 'Foil plus spiral copper shield',
    dielectric: 'Polyethylene',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e2a03f',
    tags: ['unbalanced', 'shielded', 'mid-line'],
    notes:
      'A larger centre conductor and a double screen over the same coaxial arrangement. The second screen layer raises coverage, which is an interference-rejection specification rather than a transfer-function one; the transfer function is set by the capacitance, and the engine works that out from the geometry and the length in use.',
  }),

  makeCable({
    mfr: 'Oehlbach',
    model: 'XXL Fusion Two',
    cableType: 'interconnect',
    price: usd(250, 'CURRENT_RETAIL', 2024, 'Price for a 1 m terminated RCA pair.'),
    priceLengthM: 1,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 21,
    shielding: 'Foil plus braid',
    dielectric: 'Polyethylene',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Series placement in the top XXL range and the coaxial RCA construction follow the maker listing; conductor cross-section and dielectric are not published in detail, so the gauge is estimated from the range below it. No electrical figures are published, so resistance, inductance and capacitance are derived.',
    accent: '#e2a03f',
    tags: ['unbalanced', 'shielded', 'flagship'],
    notes:
      'The top of the single-ended range, with heavier machined connector bodies and a foil-plus-braid screen over a coaxial core. The connector work is real engineering against contact resistance and mechanical fit; the modelled electrical behaviour is that of a 1 m coaxial interconnect, which is to say essentially transparent into any normal line input.',
  }),

  makeCable({
    mfr: 'Oehlbach',
    model: 'Powercord One',
    cableType: 'power',
    price: usd(90, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    currentRatingA: 16,
    termination: ['IEC C13', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e2a03f',
    tags: ['schuko', 'iec-c13', 'entry-level'],
    notes:
      'Three 1.5 mm2 OFC conductors, moulded Schuko to C13, 16 A. A correctly rated replacement mains lead. The engine applies no signal processing to power cords: this selection changes the bill of materials and leaves the audio unaltered.',
  }),

  makeCable({
    mfr: 'Oehlbach',
    model: 'Powercord Two',
    cableType: 'power',
    price: usd(160, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    shielding: 'Aluminium foil with a drain wire',
    termination: ['IEC C13', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e2a03f',
    tags: ['schuko', 'shielded'],
    notes:
      'The 2.5 mm2 conductor version with a foil screen and a drain wire. Larger conductors mean less voltage drop under load and the screen reduces radiated coupling to nearby leads, both of which are ordinary cable engineering. No DSP is applied to this cable type.',
  }),

  makeCable({
    mfr: 'Oehlbach',
    model: 'USB Max',
    cableType: 'usb',
    price: usd(55, 'CURRENT_RETAIL', 2024, 'Price for a 1 m assembly.'),
    priceLengthM: 1,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e2a03f',
    tags: ['usb-2.0', 'shielded'],
    notes:
      'A moulded 90 ohm USB 2.0 lead with gold-plated contacts and a foil-and-braid screen. USB audio is packetised and error-checked and the DAC generates its own clock in asynchronous mode, so a compliant cable carries the data or the link fails. The engine applies no signal processing to USB.',
  }),

  // =========================================================================
  // Goldkabel -- Germany. Profi / Chorus / Edition / Executive tiers, named by
  // application: LS for loudspeaker, NF for line level.
  // =========================================================================
  makeCable({
    mfr: 'Goldkabel',
    model: 'Profi LS 225',
    cableType: 'speaker',
    price: usd(45, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m pair cut from bulk cable.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 13,
    dielectric: 'PVC',
    termination: ['bare', 'banana'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Series tier and conductor cross-section are read from the model designation, which follows the usual German LS convention of two conductors of 2.5 mm2. The maker publishes no resistance, inductance or capacitance, so all three are derived from gauge, material and geometry.',
    accent: '#b8860b',
    tags: ['bulk-cable', 'entry-level', 'zip-cord'],
    notes:
      'Installer-grade bonded twin OFC at 2.5 mm2 per leg. Roughly 0.04 ohm of loop resistance over 3 m, and the bonded parallel profile keeps the conductors close, which is the low-inductance and high-capacitance end of the geometry trade.',
  }),

  makeCable({
    mfr: 'Goldkabel',
    model: 'Profi LS 240',
    cableType: 'speaker',
    price: usd(70, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m pair cut from bulk cable.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 11,
    dielectric: 'PVC',
    termination: ['bare', 'banana', 'spade'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Series tier and conductor cross-section are read from the model designation, two conductors of 4.0 mm2. No electrical figures are published, so resistance, inductance and capacitance are derived from gauge, material and geometry.',
    accent: '#b8860b',
    tags: ['bulk-cable', 'heavy-gauge', 'zip-cord'],
    notes:
      'The 4.0 mm2 installer cable, about 11 AWG per leg and roughly 0.025 ohm of loop resistance over 3 m. Chosen for long runs, where the resistance term grows linearly with length while the price of copper does the same.',
  }),

  makeCable({
    mfr: 'Goldkabel',
    model: 'Chorus LS 440',
    cableType: 'speaker',
    price: usd(220, 'APPROXIMATE', 2024, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 8,
    dielectric: 'Polyethylene',
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Series tier and conductor count are read from the model designation, four conductors of 4.0 mm2 arranged for single-wire or bi-wire use. Entered as the 8 AWG equivalent that results when the four are paralleled two per leg. No electrical figures are published, so all three are derived.',
    accent: '#b8860b',
    tags: ['bi-wire', 'four-conductor'],
    notes:
      'Four 4.0 mm2 conductors in one jacket, which can be split for bi-wiring or paralleled two per leg for a single-wire run. Paralleled, the effective conductor is about 8 AWG and the loop resistance over 3 m falls near 0.012 ohm. Bi-wired, each pair carries its own crossover section and the resistance per leg is that of a single 4.0 mm2 conductor instead.',
  }),

  makeCable({
    mfr: 'Goldkabel',
    model: 'Executive LS 640',
    cableType: 'speaker',
    price: usd(500, 'APPROXIMATE', 2024, 'Approximate price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 9,
    dielectric: 'Polyethylene',
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Series tier and cross-section are read from the model designation, two conductors of 6.0 mm2. Construction beyond conductor size is not published in detail and no electrical figures are, so resistance, inductance and capacitance are derived.',
    accent: '#b8860b',
    tags: ['heavy-gauge', 'mid-line'],
    notes:
      'Six square millimetres per leg, near 9 AWG, twisted under a PE jacket. Loop resistance over 3 m is about 0.016 ohm, giving roughly 0.03 dB of loss into a 4 ohm load. Beyond this cross-section the resistive term is no longer the thing limiting the result.',
  }),

  makeCable({
    mfr: 'Goldkabel',
    model: 'Edition NF RCA',
    cableType: 'interconnect',
    price: usd(180, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Foil plus spiral copper shield',
    dielectric: 'Polyethylene',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Series tier and application are from the catalogue naming; conductor cross-section and dielectric are estimated from the class of cable rather than published figures. No resistance, inductance or capacitance is published, so all three are derived.',
    accent: '#b8860b',
    tags: ['unbalanced', 'shielded'],
    notes:
      'Single-ended line cable with a stranded OFC core and a double screen. On an unbalanced connection the screen is also the return conductor, so its coverage and its resistance both matter; the capacitance it forms with the core is the term the engine carries into the low-pass calculation.',
  }),

  makeCable({
    mfr: 'Goldkabel',
    model: 'Executive NF XLR',
    cableType: 'interconnect',
    price: usd(350, 'APPROXIMATE', 2024, 'Approximate price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Foil plus braid',
    dielectric: 'Polyethylene',
    termination: ['XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    provenanceNote:
      'Series tier and application are from the catalogue naming; conductor cross-section is estimated from the class of cable. No electrical figures are published, so resistance, inductance and capacitance are derived from gauge, material and geometry.',
    accent: '#b8860b',
    tags: ['balanced', 'shielded'],
    notes:
      'Balanced version of the same idea: a twisted signal pair with the screen freed from return duty and connected at one end. The twist is what makes the pair reject induced noise, since both conductors enclose the same interfering flux and the receiver subtracts them.',
  }),

  makeCable({
    mfr: 'Goldkabel',
    model: 'Edition Powercord',
    cableType: 'power',
    price: usd(250, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    shielding: 'Aluminium foil with a drain wire',
    termination: ['IEC C13', 'Schuko'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Series tier and application are from the catalogue naming; conductor cross-section is estimated from the 16 A European rating this class of cord is built to. Electrical figures are not published and are not used for this cable type in any case.',
    accent: '#b8860b',
    tags: ['schuko', 'shielded'],
    notes:
      'Three 2.5 mm2 conductors with a foil screen, Schuko to C13, 16 A. Construction description only: the engine applies no signal processing to mains cords, so nothing here is claimed about how it sounds because nothing can be.',
  }),

  makeCable({
    mfr: 'Goldkabel',
    model: 'Executive Powercord',
    cableType: 'power',
    price: usd(450, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 16,
    shielding: 'Foil plus braid',
    termination: ['IEC C13', 'IEC C19', 'Schuko'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Series tier and application are from the catalogue naming; conductor cross-section is estimated from the class of cord. No electrical figures are published, and none are applied to power cables by the engine.',
    accent: '#b8860b',
    tags: ['schuko', 'shielded', 'high-current'],
    notes:
      'Four square millimetre conductors with a double screen and a C19 option for amplifiers with the larger inlet. More copper means less drop under a heavy transient draw, which is a supply-side argument and stops there. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Goldkabel',
    model: 'Edition USB',
    cableType: 'usb',
    price: usd(90, 'APPROXIMATE', 2024, 'Approximate price for a 1 m assembly.'),
    priceLengthM: 1,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid',
    termination: ['USB A to B'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Series tier and application are from the catalogue naming; the 90 ohm differential pair and 24 AWG conductors are the requirements of the USB 2.0 specification this cable is sold as meeting, rather than separately published figures.',
    accent: '#b8860b',
    tags: ['usb-2.0', 'shielded'],
    notes:
      'A screened USB 2.0 assembly with gold-plated contacts, built to the 90 ohm differential requirement. Compliance is the specification; the engine applies no signal processing to USB cables.',
  }),

  // =========================================================================
  // Vovox -- Switzerland. Solid-core conductors, minimal dielectric, and an
  // unusually literal division between unshielded (direct) and shielded
  // (protect) versions of the same cable.
  // =========================================================================
  makeCable({
    mfr: 'Vovox',
    model: 'sonorus direct S',
    cableType: 'interconnect',
    price: usd(400, 'CURRENT_RETAIL', 2024, 'Price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Switzerland',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 20,
    shielding: 'None; unshielded by design',
    dielectric: 'Textile and polyethylene, minimal contact with the conductor',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8d99ae',
    tags: ['unshielded', 'solid-core', 'hand-assembled'],
    notes:
      'Solid-core copper conductors in a loose textile-and-PE dielectric, with no screen at all. Wide spacing and a low-permittivity surround put this at the low-capacitance, high-inductance end of the geometry table, which for a line-level cable means the low-pass corner sits further out than a coaxial lead. The absence of a shield is the real trade and it is a practical one: rejection depends entirely on the balanced connection and on keeping the run away from mains wiring.',
  }),

  makeCable({
    mfr: 'Vovox',
    model: 'sonorus protect A',
    cableType: 'interconnect',
    price: usd(500, 'CURRENT_RETAIL', 2024, 'Price for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Switzerland',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 20,
    shielding: 'Spiral copper shield',
    dielectric: 'Textile and polyethylene',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8d99ae',
    tags: ['shielded', 'solid-core', 'hand-assembled'],
    notes:
      'The screened version of the same solid-core construction, for unbalanced connections and for runs that cannot be routed away from interference. Adding a screen around the conductors raises capacitance relative to the unshielded version, since capacitance depends on the proximity of a return path -- the shield is not free, it is a trade for immunity.',
  }),

  makeCable({
    mfr: 'Vovox',
    model: 'link direct S',
    cableType: 'interconnect',
    price: usd(300, 'CURRENT_RETAIL', 2024, 'Price for a 1 m terminated lead.'),
    priceLengthM: 1,
    country: 'Switzerland',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 20,
    shielding: 'None; unshielded by design',
    dielectric: 'Textile and polyethylene',
    termination: ['TS jack'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8d99ae',
    tags: ['unshielded', 'solid-core', 'instrument'],
    notes:
      'The instrument-length version of the unshielded solid-core cable. Capacitance is the parameter that matters most for a high-impedance instrument source, because it forms a low-pass with the pickup impedance that can land inside the audio band; a low-capacitance construction pushes that corner up. Against a line-level source the same figure is inconsequential, and the engine reports whichever case is actually connected.',
  }),

  makeCable({
    mfr: 'Vovox',
    model: 'link protect A',
    cableType: 'interconnect',
    price: usd(350, 'CURRENT_RETAIL', 2024, 'Price for a 1 m terminated lead.'),
    priceLengthM: 1,
    country: 'Switzerland',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 20,
    shielding: 'Spiral copper shield used as the signal return',
    dielectric: 'Textile and polyethylene',
    termination: ['TS jack'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8d99ae',
    tags: ['shielded', 'instrument'],
    notes:
      'Screened instrument lead with the shield serving as the return, which makes it coaxial in the electrical sense. Concentric geometry gives the small loop area and the higher capacitance that goes with it. For a guitar pickup that capacitance is a real load; for a DAC output at a few hundred ohms it is not.',
  }),

  makeCable({
    mfr: 'Vovox',
    model: 'excelsus drive',
    cableType: 'speaker',
    price: usd(1200, 'CURRENT_RETAIL', 2024, 'Price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'Switzerland',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 13,
    dielectric: 'Textile and polyethylene',
    termination: ['banana', 'spade', 'bare'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'The solid-core, minimal-dielectric construction is the stated basis of the whole range and the loudspeaker cables follow it; the exact conductor cross-section is not published, so the gauge is estimated from the cable diameter and the intended load. No electrical figures are published, so all three are derived.',
    accent: '#8d99ae',
    tags: ['solid-core', 'hand-assembled'],
    notes:
      'Solid-core conductors carried in a loose dielectric rather than extruded into one. That spacing puts the cable at the high-inductance end of the table: over 3 m the derived series reactance at 20 kHz is a few tenths of an ohm, which into a 4 ohm load is under a tenth of a decibel but is not zero, and the resistive term at this cross-section is the larger of the two.',
  }),

  makeCable({
    mfr: 'Vovox',
    model: 'fortis drive',
    cableType: 'speaker',
    price: usd(2000, 'CURRENT_RETAIL', 2024, 'Price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'Switzerland',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 11,
    dielectric: 'Textile and polyethylene',
    termination: ['banana', 'spade', 'bare'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Series placement above the smaller loudspeaker cable and the shared solid-core construction are from the maker description; the conductor cross-section is estimated rather than published. Resistance, inductance and capacitance are all derived.',
    accent: '#8d99ae',
    tags: ['solid-core', 'heavy-gauge', 'flagship'],
    notes:
      'The larger loudspeaker cable in the range, with more copper per leg in the same solid-core, loosely dressed construction. The extra cross-section takes about a third off the loop resistance relative to the smaller version, roughly 0.025 ohm over 3 m; the inductance is set by geometry and stays where the spacing puts it.',
  }),

  // =========================================================================
  // Sommer Cable -- Germany. Full-range pro-audio house; bulk cable that gets
  // terminated to order, which is why the prices below are per assembled pair.
  // =========================================================================
  makeCable({
    mfr: 'Sommer Cable',
    model: 'SC-Galileo 238',
    cableType: 'interconnect',
    price: usd(90, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m terminated pair built from bulk cable.'),
    priceLengthM: 1,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Spiral copper shield',
    dielectric: 'Polyethylene',
    termination: ['XLR', 'RCA'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e63946',
    tags: ['pro-audio', 'bulk-cable', 'studio-standard'],
    notes:
      'Studio line cable with two 0.38 mm2 conductors, about 21 AWG, twisted under a spiral copper screen. The larger-than-usual conductor is aimed at long fixed installations where termination reliability matters more than flexibility. Electrically it is a well-behaved twisted pair and the derived capacitance places its corner far above the audio band at any studio length.',
  }),

  makeCable({
    mfr: 'Sommer Cable',
    model: 'SC-Stage 22 Highflex',
    cableType: 'interconnect',
    price: usd(70, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m terminated pair built from bulk cable.'),
    priceLengthM: 1,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    shielding: 'Spiral copper shield',
    dielectric: 'Polyethylene',
    termination: ['XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e63946',
    tags: ['pro-audio', 'bulk-cable', 'flexible'],
    notes:
      'The touring microphone cable: two 0.22 mm2 conductors of fine strands for flexibility, spiral screen, rubber-like jacket. Fine stranding is a mechanical decision about flex life; it leaves the DC resistance where the total cross-section puts it, near 0.17 ohm per metre of loop.',
  }),

  makeCable({
    mfr: 'Sommer Cable',
    model: 'SC-Source MKII Highflex',
    cableType: 'interconnect',
    price: usd(75, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m terminated pair built from bulk cable.'),
    priceLengthM: 1,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 23,
    shielding: 'Spiral copper shield',
    dielectric: 'Polyethylene',
    termination: ['XLR', 'RCA'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e63946',
    tags: ['pro-audio', 'bulk-cable', 'flexible'],
    notes:
      'A slightly heavier conductor than the stage cable in the same flexible jacket, intended for permanent patching as well as stage use. Nothing in this construction is unusual and nothing needs to be: a screened twisted pair at line level is a solved problem, and the engine says so by computing a response deviation of essentially zero.',
  }),

  makeCable({
    mfr: 'Sommer Cable',
    model: 'SC-Isopod SO-F22',
    cableType: 'interconnect',
    price: usd(95, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m terminated pair built from bulk cable.'),
    priceLengthM: 1,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'star-quad',
    gauge: 21,
    shielding: 'Spiral copper shield',
    dielectric: 'Polyethylene',
    termination: ['XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e63946',
    tags: ['pro-audio', 'bulk-cable', 'hum-rejection'],
    notes:
      'Four 0.22 mm2 conductors in a quad, paralleled in opposite pairs, which is the standard answer to magnetically induced hum near lighting bars and mains transformers. Paralleling halves the resistance per leg, so the effective conductor is about 21 AWG despite each wire being 24.',
  }),

  makeCable({
    mfr: 'Sommer Cable',
    model: 'SC-Club Series MKII',
    cableType: 'interconnect',
    price: usd(60, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m terminated lead built from bulk cable.'),
    priceLengthM: 1,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Spiral copper shield used as the signal return',
    dielectric: 'Polyethylene',
    termination: ['TS jack', 'RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e63946',
    tags: ['pro-audio', 'unbalanced', 'instrument'],
    notes:
      'Unbalanced instrument and patch cable: single core, PE dielectric, spiral screen as return, with a conductive layer under the screen to suppress the triboelectric noise a coaxial cable generates when it is flexed. That handling-noise layer is a genuine construction feature with a mechanical mechanism, distinct from anything in the audio band.',
  }),

  makeCable({
    mfr: 'Sommer Cable',
    model: 'The Spirit LLx',
    cableType: 'interconnect',
    price: usd(80, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m terminated lead built from bulk cable.'),
    priceLengthM: 1,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Spiral copper shield used as the signal return',
    dielectric: 'Foamed polyethylene',
    termination: ['TS jack'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e63946',
    tags: ['pro-audio', 'unbalanced', 'instrument', 'low-capacitance'],
    notes:
      'An instrument cable built specifically for low capacitance, using a foamed dielectric that traps air and so lowers the effective permittivity between core and screen. For a magnetic pickup with a high source impedance this is the one cable parameter that can move a corner frequency into the audio band, which is why the design targets it. Driven from a line output the same cable is electrically uneventful.',
  }),

  makeCable({
    mfr: 'Sommer Cable',
    model: 'SC-Meridian SP240',
    cableType: 'speaker',
    price: usd(110, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m terminated pair built from bulk cable.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    dielectric: 'Polyethylene',
    termination: ['banana', 'spade', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e63946',
    tags: ['pro-audio', 'bulk-cable', 'heavy-gauge'],
    notes:
      'Two 4.0 mm2 conductors, about 11 AWG each, twisted in a rugged jacket. Loop resistance over 3 m is roughly 0.025 ohm, or about 0.07 dB at a 3 ohm impedance minimum. This is the cable a PA company buys by the drum, and electrically it is the equal of far more expensive domestic designs of the same cross-section.',
  }),

  makeCable({
    mfr: 'Sommer Cable',
    model: 'SC-Orbit 240 MKII',
    cableType: 'speaker',
    price: usd(130, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m terminated pair built from bulk cable.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    dielectric: 'Polyethylene',
    termination: ['banana', 'spade', 'bare', 'speakON'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e63946',
    tags: ['pro-audio', 'bulk-cable', 'touring'],
    notes:
      'Also 2 x 4.0 mm2, in a more flexible jacket for repeated coiling and with speakON termination as the usual option. Same conductor area means the same derived loop resistance as the installation cable above it; what differs is jacket compound and flex life, which the model has no term for and which is not pretended otherwise.',
  }),

  makeCable({
    mfr: 'Sommer Cable',
    model: 'SC-Elephant SPM440',
    cableType: 'speaker',
    price: usd(220, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m terminated pair built from bulk cable.'),
    priceLengthM: 3,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'star-quad',
    gauge: 8,
    dielectric: 'Polyethylene',
    termination: ['speakON', 'banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e63946',
    tags: ['pro-audio', 'bulk-cable', 'bi-amp'],
    notes:
      'Four 4.0 mm2 conductors, normally used as two circuits for bi-amping or paralleled two per leg for one. Paralleled, the effective cross-section is 8 mm2 and the loop resistance over 3 m falls to about 0.012 ohm, which is the configuration entered here. Long throws on a stage are where that cross-section is bought.',
  }),

  makeCable({
    mfr: 'Sommer Cable',
    model: 'SC-Mercator CAT.7',
    cableType: 'ethernet',
    price: usd(50, 'APPROXIMATE', 2024, 'Approximate cost of a 2 m assembled patch lead.'),
    priceLengthM: 2,
    country: 'Germany',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 23,
    characteristicOhms: 100,
    digitalStandard: 'Cat 7, S/FTP, 600 MHz',
    shielding: 'S/FTP, individually foiled pairs in an overall braid',
    termination: ['RJ45', 'etherCON'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e63946',
    tags: ['pro-audio', 'cat7', 'shielded'],
    notes:
      'Four individually foiled 23 AWG pairs in an overall braid, certified to Cat 7 and normally terminated in etherCON housings for stage use. Built for Dante and AES67 racks where a link either passes its bit error rate test or does not. The engine applies no signal processing to Ethernet cables.',
  }),
];
